import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Banknote } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { money } from "@/lib/analytics";
import { DeleteButton } from "@/components/DeleteButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const EMPTY = { company_name: "", amount: "", payback: "", payment_plan: "weekly" };

export default function CashAdvance() {
  const [advances, setAdvances] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  useEffect(() => { load(); }, []);
  async function load() { try { const { data } = await api.get("/cash-advances"); setAdvances(data); } catch { /* */ } }

  async function del(id) {
    try { await api.delete(`/cash-advances/${id}`); toast.success("Cash advance deleted."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete."); }
  }

  async function submit(e) {
    e.preventDefault(); setLoading(true);
    try {
      await api.post("/cash-advances", { ...form, amount: Number(form.amount), payback: Number(form.payback) });
      toast.success("Cash advance logged."); setForm(EMPTY); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
    finally { setLoading(false); }
  }

  // weekly & monthly payback cost from payment plans
  let weekly = 0, monthly = 0;
  advances.forEach((a) => {
    if (a.payment_plan === "daily") { weekly += a.payback * 7; monthly += a.payback * 30; }
    else if (a.payment_plan === "weekly") { weekly += a.payback; monthly += a.payback * 4; }
    else { weekly += a.payback / 4; monthly += a.payback; }
  });
  const chartData = [{ label: "Weekly", value: Math.round(weekly) }, { label: "Monthly", value: Math.round(monthly) }];

  return (
    <div>
      <h1 className="font-heading text-3xl font-bold tracking-tight">Cash Advances</h1>
      <p className="mt-1 text-sm text-muted-foreground">Track advances taken and projected weekly / monthly payback costs.</p>

      <form onSubmit={submit} className="mt-6 grid gap-3 rounded-xl border border-border/60 bg-card p-5 sm:grid-cols-2 lg:grid-cols-5">
        <div className="space-y-1.5 lg:col-span-2"><Label className="text-xs">Company</Label><Input data-testid="ca-company" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} required /></div>
        <div className="space-y-1.5"><Label className="text-xs">Amount ($)</Label><Input type="number" step="0.01" data-testid="ca-amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required /></div>
        <div className="space-y-1.5"><Label className="text-xs">Payback ($)</Label><Input type="number" step="0.01" data-testid="ca-payback" value={form.payback} onChange={(e) => setForm({ ...form, payback: e.target.value })} required /></div>
        <div className="space-y-1.5"><Label className="text-xs">Plan</Label>
          <Select value={form.payment_plan} onValueChange={(v) => setForm({ ...form, payment_plan: v })}>
            <SelectTrigger data-testid="ca-plan"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="daily">Daily</SelectItem><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="monthly">Monthly</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="lg:col-span-5"><Button type="submit" disabled={loading} data-testid="ca-submit" className="rounded-full"><Plus className="mr-1 h-4 w-4" /> Log Cash Advance</Button></div>
      </form>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-border/60 bg-card p-5">
          <h3 className="mb-4 font-heading font-bold">Projected Payback Cost</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="label" /><YAxis /><Tooltip formatter={(v) => money(v)} /><Legend />
              <Bar dataKey="value" name="Payback" fill="hsl(175 84% 32%)" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
          <Table>
            <TableHeader><TableRow><TableHead>Company</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Payback</TableHead><TableHead>Plan</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {advances.length === 0 ? <TableRow><TableCell colSpan={5} className="py-8 text-center text-muted-foreground">No advances logged.</TableCell></TableRow>
                : advances.map((a) => (
                  <TableRow key={a.id} data-testid={`ca-row-${a.id}`}>
                    <TableCell className="font-medium">{a.company_name}</TableCell>
                    <TableCell className="text-right">{money(a.amount)}</TableCell>
                    <TableCell className="text-right">{money(a.payback)}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{a.payment_plan}</Badge></TableCell>
                    <TableCell className="text-right"><DeleteButton onConfirm={() => del(a.id)} label="this cash advance" testId={`ca-delete-${a.id}`} /></TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
